import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Select from 'react-select';
import { baseUrl, getDateWithDay } from '../comman/config';
import { menuAboveTable } from "../comman/selectMenu";

export default function ActualStockEntry() {
    const [formData, setFormData] = useState({
        ingredient_id: null,
        quantity: '',
        unit: '',
        unit_name: ''
    });

    const [ingredientsList, setIngredientsList] = useState([]);
    const [lastEntries, setLastEntries] = useState([]);
    const [validationErrors, setValidationErrors] = useState({
        ingredient_id: false,
        quantity: false
    });
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [selectedIngredient, setSelectedIngredient] = useState(null);

    useEffect(() => {
        getIngredientsList();
    }, []);

    // Clear error and success messages after 4 seconds
    useEffect(() => {
        if (error || success) {
            const timer = setTimeout(() => {
                setError(null);
                setSuccess(null);
            }, 4000);
            return () => clearTimeout(timer);
        }
    }, [error, success]);

    const getIngredientsList = async () => {
        try {
            const response = await axios.get(`${baseUrl}/actual_stock/list?condition=status=1`);
            setIngredientsList(response.data.data);
        } catch (error) {
            console.error("Error fetching ingredients:", error);
            setError("Failed to load ingredients list");
        }
    };

    const getLastEntries = async (ingredientId) => {
        try {
            const response = await axios.get(`${baseUrl}/actual_stock/last_entries`, {
                params: { ingredientId }
            });

            setLastEntries(response.data.data);
            console.log("lastentries:",lastEntries)
        } catch (error) {
            console.error("Error fetching last entries:", error);
            setError("Failed to load previous entries");
        }
    };

    const handleIngredientChange = (selectedOption) => {
        setFormData((prevData) => ({
            ...prevData,
            ingredient_id: selectedOption,
            quantity: '', // Reset quantity when ingredient changes
            unit: selectedOption?.unit || '', // Set unit from selected ingredient
            unit_name: selectedOption?.unit_name || ''
        }));

        setSelectedIngredient(selectedOption?.label || '');
        setValidationErrors((prevErrors) => ({
            ...prevErrors,
            ingredient_id: false
        }));

        if (selectedOption?.value) {
            getLastEntries(selectedOption.value);
        } else {
            setLastEntries([]);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;

        setFormData((prevData) => ({
            ...prevData,
            [name]: value
        }));

        setValidationErrors((prevErrors) => ({
            ...prevErrors,
            [name]: false
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const newValidationErrors = {
            ingredient_id: !formData.ingredient_id,
            quantity: formData.quantity === ''
        };

        setValidationErrors(newValidationErrors);

        const hasErrors = Object.values(newValidationErrors).some((error) => error);

        if (hasErrors) {
            setError("Please fill all required fields");
            return;
        }

        try {
            const currentDate = new Date();
            const formattedDate = currentDate.toISOString().split('T')[0];
            const hours = currentDate.getHours();
            const minutes = currentDate.getMinutes();
            const timeStamp = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')} ${hours >= 12 ? 'pm' : 'am'}`;

            const stockData = {
                ingredient_id: formData.ingredient_id.value,
                date: formattedDate,
                time_stamp: timeStamp,
                quantity: formData.quantity,
                unit: formData.ingredient_id.unit
            };

            const response = await axios.post(`${baseUrl}/actual_stock/add`, stockData);

            if (response.data.status) {
                setSuccess("Stock entry saved successfully");
                // Refresh the last entries
                getLastEntries(formData.ingredient_id.value);
                // Reset quantity
                setFormData(prev => ({
                    ...prev,
                    quantity: ''
                }));
            } else {
                setError(response.data.msg || "Failed to save stock entry");
            }
        } catch (error) {
            console.error("Error saving stock entry:", error);
            setError("An error occurred while saving the stock entry");
        }
    };

    const handleCancel = () => {
        setFormData({
            ingredient_id: null,
            quantity: '',
            unit: '',
            unit_name: ''
        });
        setSelectedIngredient(null);
        setLastEntries([]);
        setValidationErrors({
            ingredient_id: false,
            quantity: false
        });
    };

    return (
        <>
            {/* Error Alert */}
            {error && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4" role="alert">
                    <span className="block sm:inline">{error}</span>
                    <span className="absolute top-0 bottom-0 right-0 px-4 py-3" onClick={() => setError(null)}>
                        <svg className="fill-current h-6 w-6 text-red-500" role="button" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                            <title>Close</title>
                            <path d="M14.348 14.849a1.2 1.2 0 0 1-1.697 0L10 11.819l-2.651 3.029a1.2 1.2 0 1 1-1.697-1.697l2.758-3.15-2.759-3.152a1.2 1.2 0 1 1 1.697-1.697L10 8.183l2.651-3.031a1.2 1.2 0 1 1 1.697 1.697l-2.758 3.152 2.758 3.15a1.2 1.2 0 0 1 0 1.698z" />
                        </svg>
                    </span>
                </div>
            )}

            {/* Success Alert */}
            {success && (
                <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative mb-4" role="alert">
                    <span className="block sm:inline">{success}</span>
                    <span className="absolute top-0 bottom-0 right-0 px-4 py-3" onClick={() => setSuccess(null)}>
                        <svg className="fill-current h-6 w-6 text-green-500" role="button" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                            <title>Close</title>
                            <path d="M14.348 14.849a1.2 1.2 0 0 1-1.697 0L10 11.819l-2.651 3.029a1.2 1.2 0 1 1-1.697-1.697l2.758-3.15-2.759-3.152a1.2 1.2 0 1 1 1.697-1.697L10 8.183l2.651-3.031a1.2 1.2 0 1 1 1.697 1.697l-2.758 3.152 2.758 3.15a1.2 1.2 0 0 1 0 1.698z" />
                        </svg>
                    </span>
                </div>
            )}

            <div className="bg-white shadow-md rounded-lg p-6">
                <h1 className="text-2xl font-bold mb-6 border-b pb-2">Actual Stock Entry Screen</h1>

                <div className="grid grid-cols-1 sm:grid-cols-6 gap-4 mb-6">
                    <div className="sm:col-span-2">
                        <label htmlFor="ingredient" className="block text-sm font-medium text-gray-700 mb-1">Ingredient Name</label>
                        <Select
                            {...menuAboveTable}
                            className={`basic-single ${validationErrors.ingredient_id ? 'border-red-500' : ''}`}
                            classNamePrefix="select"
                            value={formData.ingredient_id}
                            onChange={handleIngredientChange}
                            options={ingredientsList.map(item => ({
                                value: item.id,
                                label: `${item.ingredient} (${item.eng_name})`,
                                unit: item.unit,
                                unit_name: item.unit_name
                            }))}
                            placeholder="Ingredient"
                            noOptionsMessage={() => "No ingredients found"}
                            isClearable
                            filterOption={(option, inputValue) => {
                                // Fix: Add null check before calling toLowerCase()
                                if (!inputValue || !option.data.label) {
                                    return true;
                                }
                                return option.data.label.toLowerCase().includes(inputValue.toLowerCase());
                            }}
                            // Additionally, add a minimum input length to avoid errors
                            isSearchable={true}
                            minInputLength={3}
                        />
                        {validationErrors.ingredient_id && <div className="text-red-500 text-sm mt-1">Please select an ingredient</div>}
                    </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-6 gap-4 mb-6">
                    <div className="sm:col-span-1">
                        <label htmlFor="quantity" className="block text-sm font-medium text-gray-700 mb-1">Actual Stock Qty.</label>
                        <input
                            type="number"
                            id="quantity"
                            name="quantity"
                            value={formData.quantity}
                            onChange={handleInputChange}
                            className={`block w-full rounded-md border ${validationErrors.quantity ? 'border-red-500' : 'border-gray-300'} py-1.5 px-3 text-gray-900 shadow-sm placeholder:text-gray-400 sm:text-sm`}
                            placeholder="To enter total qty."
                        />
                        {validationErrors.quantity && <div className="text-red-500 text-sm mt-1">Please enter quantity</div>}
                    </div>
                    <div className="sm:col-span-1">
                        <label htmlFor="unit" className="block text-sm font-medium text-gray-700 mb-1">UNIT</label>
                        <input
                            type="text"
                            id="unit"
                            name="unit"
                            value={formData.unit_name}
                            readOnly
                            className="block w-full rounded-md border border-gray-300 bg-gray-100 py-1.5 px-3 text-gray-900 shadow-sm placeholder:text-gray-400 sm:text-sm"
                        />
                    </div>
                </div>

                <div className="flex justify-center gap-4 mb-8">
                    <button
                        onClick={handleSubmit}
                        className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:outline-none focus:ring focus:ring-blue-200 disabled:bg-blue-300"
                    >
                        Save
                    </button>
                    <button
                        onClick={handleCancel}
                        className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:outline-none focus:ring focus:ring-blue-200 disabled:bg-blue-300"
                    >
                        Cancel
                    </button>
                </div>

                {lastEntries.length > 0 && (
                    <div className="mt-8">

                        <div className="ingredient-name mb-2">
                            <span className="font-medium">Ingredient Name: </span>
                            <span>{selectedIngredient}</span>
                        </div>

                        <div className="overflow-x-auto">
                            <table className="min-w-full bg-white border border-gray-300">
                                <thead>
                                    <tr>
                                        <th className="border border-gray-300 px-4 py-2 w-1/4">Date</th>
                                        <th className="border border-gray-300 px-4 py-2 w-1/4">Time stamp</th>
                                        <th className="border border-gray-300 px-4 py-2 w-1/4">Qty.</th>
                                        <th className="border border-gray-300 px-4 py-2 w-1/4">Unit</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {lastEntries.map((entry, index) => (
                                        <tr key={index}>
                                            <td className="border border-gray-300 px-4 py-2 text-center">{getDateWithDay(entry.entry_date)}</td>
                                            <td className="border border-gray-300 px-4 py-2 text-center">{entry.time_stamp}</td>
                                            <td className="border border-gray-300 px-4 py-2 text-center">{entry.quantity}</td>
                                            <td className="border border-gray-300 px-4 py-2 text-center">{entry.unit_name}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </div>
        </>
    );
}